import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useT } from "@/i18n";
import { adjustArticle, fetchArticleStocks, type ArticleRow } from "@/services/articleStocks";
import { articleName, formatPrice } from "@/services/articles";
import ArticleSheetDialog from "@/components/ArticleSheetDialog";
import { ConfirmModal, QtyControls } from "@/components/QtyEntry";
import { ReadOnlyArea } from "@/components/ReadOnly";

// Z Production · "Stock des articles" (his `renderItemStock`): every article code ONCE, grouped under
// the kit that uses it — or "linen shared by several sizes" — with what Penthalaz holds, how many kits
// it makes, his catalogue chips, and the same entry, confirm and toast as the kits.
const ENTRY_KEY = "myotaku-warehouse:item-entry";

export default function ArticleStockCard({ warehouseId }: { warehouseId: number | null }) {
  const { t, locale } = useT();
  const qc = useQueryClient();
  // His article stock is Penthalaz's; a chosen warehouse is shown as it is.
  const wh = warehouseId ?? 1;
  const [sheet, setSheet] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [mode, setModeState] = useState<"add" | "set">(() => { try { return localStorage.getItem(ENTRY_KEY) === "set" ? "set" : "add"; } catch { return "add"; } });
  const setMode = (v: "add" | "set") => { setModeState(v); try { localStorage.setItem(ENTRY_KEY, v); } catch { /* this tab only */ } };
  const [ask, setAsk] = useState<null | { a: ArticleRow; prev: number; next: number; body: { delta?: number; set?: number; mode?: "plus_minus" } }>(null);
  const [toast, setToast] = useState<null | { ok: boolean; detail: string }>(null);
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), toast.ok ? 3500 : 8000);
    return () => clearTimeout(id);
  }, [toast]);
  const q = useQuery({ queryKey: ["article-stocks", wh], queryFn: () => fetchArticleStocks(wh) });
  const name = (ref: string) => (articleName(ref, locale) ?? "").replace(/\s*\(140-200\)/g, "");
  const needle = search.trim().toLowerCase();
  const matches = (a: ArticleRow) => !needle || [a.reference, name(a.reference), a.catalog?.myotaku_sku, a.catalog?.supplier, a.catalog?.supplier_sku]
    .some((x) => (x ?? "").toLowerCase().includes(needle));
  const groups = (q.data?.groups ?? []).map((g) => ({ ...g, shown: g.articles.filter(matches) })).filter((g) => g.shown.length > 0);
  const all = (q.data?.groups ?? []).flatMap((g) => g.articles);
  const label = (a: ArticleRow) => `${name(a.reference) || a.reference} (${a.reference})`;
  const detail = (a: ArticleRow, prev: number, next: number) => `${label(a)} : ${prev} ${next >= prev ? "+" : "−"} ${Math.abs(next - prev)} = ${next}`;
  const commit = (a: ArticleRow, op: "delta" | "set", n: number, plusMinus = false) => {
    const next = Math.max(0, Math.round(op === "delta" ? a.qty + n : n));
    if (next === a.qty) return;
    setAsk({ a, prev: a.qty, next, body: op === "set" ? { set: next } : { delta: n, mode: plusMinus ? "plus_minus" : undefined } });
  };

  return (
    <section className="rounded-xl bg-white shadow-sm ring-1 ring-slate-200">
      <h2 className="border-b border-slate-100 px-5 py-3 font-medium text-slate-900">{t("art.title")}</h2>
      <div className="space-y-2 px-5 py-3">
        <p className="text-xs text-slate-500">{t("art.hint")}</p>
        {/* The entry writes the shelf: greyed for someone who only reads Production. The search and
            each article's sheet stay open. */}
        <ReadOnlyArea module="production">
        <div className="flex flex-wrap items-center gap-1 text-sm">
          <span className="mr-1 text-xs text-slate-500">{t("prod.entry.label")}</span>
          {(["add", "set"] as const).map((v) => (
            <button key={v} onClick={() => setMode(v)} aria-pressed={mode === v}
              className={`rounded-lg px-2.5 py-1 text-xs ${mode === v ? "bg-slate-900 text-white" : "text-slate-600 ring-1 ring-slate-200"}`}>{t(`prod.entry.${v}`)}</button>
          ))}
        </div>
        </ReadOnlyArea>
        <p className="text-xs text-slate-500">{t(`prod.entry.${mode}Help`)}</p>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("art.search")}
          className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
        {q.data && <p className="text-sm text-slate-600">{t("art.total", { q: all.reduce((s, a) => s + a.qty, 0), n: all.length })}</p>}
      </div>
      {q.isError && <p className="px-5 pb-3 text-sm text-red-700">{(q.error as Error).message}</p>}
      {q.data && groups.length === 0 && <p className="px-5 pb-3 text-sm text-slate-400">{t("art.noMatch")}</p>}
      {groups.map((g) => (
        <div key={g.kit ?? "other"} className="border-t border-slate-100">
          {/* His group head: the kit's colour, its name, how many references. */}
          <p className="flex items-center gap-2 bg-slate-50 px-5 py-1.5 text-sm font-semibold text-slate-800">
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: g.color ?? "#94a3b8" }} />
            {g.kit === "LINEN_SHARED" ? t("art.linenShared") : g.label ?? t("art.other")}
            <span className="font-normal text-slate-400">{t("art.refCount", { n: g.shown.length })}</span>
          </p>
          <ul className="divide-y divide-slate-50">
            {g.shown.map((a) => {
              const qtys = a.owners.map((o) => o.qty);
              const same = qtys.every((x) => x === qtys[0]);
              const perKit = qtys.length ? (same ? qtys[0] : Math.max(...qtys)) || 1 : 1;
              // His badge: "×2 / kit", or by size when the kits differ ("×1 (90) · ×2 (140/160/180/200)").
              const badge = !qtys.length ? "" : same ? (qtys[0] > 1 ? t("art.perKit", { n: qtys[0] }) : "")
                : [...new Set(qtys)].sort((x, y) => x - y).map((n) => `×${n} (${a.owners.filter((o) => o.qty === n).map((o) => o.label.replace(/^Kit Linge\s*/i, ""))
                  .sort((x, y) => (parseInt(x, 10) || 0) - (parseInt(y, 10) || 0)).join("/")})`).join(" · ");
              const kits = Math.floor(a.qty / perKit);
              const shared = a.owners.length > 1 ? t("art.sharedBy", { list: a.owners.map((o) => o.label).join(", ") }) : "";
              return (
                <li key={a.reference} className="px-5 py-2 text-sm">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <button onClick={() => setSheet(a.reference)} title={[shared, badge, t("sheet.open")].filter(Boolean).join(" — ")}
                      className="min-w-0 text-left hover:underline">
                      <span className="font-mono text-slate-800">{a.reference}</span>
                      <span className="ml-2 text-slate-600">{name(a.reference)}</span>
                    </button>
                    {badge && <span className="rounded bg-slate-100 px-1.5 text-xs text-slate-600">{badge}</span>}
                    <div className="ml-auto flex items-center gap-2">
                      <ReadOnlyArea module="production">
                        <QtyControls mode={mode} qty={a.qty} label={label(a)}
                          onDelta={(n, pm) => commit(a, "delta", n, pm)} onSet={(n) => commit(a, "set", n)} />
                      </ReadOnlyArea>
                      {a.owners.length > 0 && (
                        <span className={`w-16 text-right text-xs font-medium tabular-nums ${kits <= 0 ? "text-red-700" : "text-emerald-700"}`}
                          title={`${a.qty} ${a.reference} ÷ ${perKit}`}>= {t(kits === 1 ? "prod.kitsOne" : "prod.kitsMany", { n: kits })}</span>
                      )}
                    </div>
                  </div>
                  {/* His catalogue chips: system SKU, supplier, supplier ref, price for the manager, "Sheet ›". */}
                  {a.catalog && (
                    <button onClick={() => setSheet(a.reference)} className="mt-1 flex flex-wrap gap-1 text-left text-[11px]">
                      {a.catalog.myotaku_sku && <span className="rounded bg-slate-100 px-1.5 font-mono text-slate-600">{t("art.chip.sku", { s: a.catalog.myotaku_sku })}</span>}
                      {a.catalog.supplier && <span className="rounded bg-slate-100 px-1.5 text-slate-600">🛒 {a.catalog.supplier}</span>}
                      {a.catalog.supplier_sku && <span className="rounded bg-slate-100 px-1.5 font-mono text-slate-600">{t("art.chip.ref", { s: a.catalog.supplier_sku })}</span>}
                      {a.catalog.price && <span className="rounded bg-emerald-50 px-1.5 text-emerald-800">{formatPrice(a.catalog.price, a.catalog.currency)}</span>}
                      {a.catalog.to_confirm && <span className="rounded bg-amber-50 px-1.5 text-amber-800">{t("art.chip.confirm")}</span>}
                      <span className="rounded px-1.5 text-sky-700">{t("art.chip.open")}</span>
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
      {ask && (
        <ConfirmModal down={ask.next < ask.prev} yes={t(ask.next > ask.prev ? "prod.entry.yesAdd" : "prod.entry.yesDown")}
          question={t(ask.next > ask.prev ? "prod.entry.askAdd" : "prod.entry.askDown", { n: Math.abs(ask.next - ask.prev), k: label(ask.a) })}
          detail={detail(ask.a, ask.prev, ask.next)}
          onNo={() => setAsk(null)}
          onYes={() => {
            const x = ask; setAsk(null);
            const d = detail(x.a, x.prev, x.next);
            adjustArticle({ warehouse_id: wh, article_reference: x.a.reference, ...x.body }).then(() => {
              setToast({ ok: true, detail: d });
              ["article-stocks", "stock-movements", "production"].forEach((k) => void qc.invalidateQueries({ queryKey: [k] }));
            }, () => setToast({ ok: false, detail: d }));
          }} />
      )}
      {toast && (
        <button onClick={() => setToast(null)} role="status"
          className={`fixed bottom-5 left-1/2 z-50 -translate-x-1/2 rounded-lg px-4 py-2.5 text-left text-sm text-white shadow-lg ${toast.ok ? "bg-emerald-700" : "bg-red-700"}`}>
          {t(toast.ok ? "prod.entry.saved" : "prod.entry.notSaved")}
          <span className="block text-xs opacity-90">{toast.detail}</span>
          <span className="block text-[11px] opacity-70">{t("prod.entry.tapClose")}</span>
        </button>
      )}
      {sheet && <ArticleSheetDialog reference={sheet} warehouseId={wh} onClose={() => setSheet(null)} />}
    </section>
  );
}
